import React from "react";
import { Sparkles, Moon, Wind, Flame, Heart, Send } from "lucide-react";

/**
 * CSS-drawn phone mockup showing My Mood Groove app concept.
 * Replace the inner content blocks freely without touching layout.
 */
const moods = [
  { icon: Moon, label: "Calm" },
  { icon: Wind, label: "Focus" },
  { icon: Flame, label: "Energy" },
  { icon: Heart, label: "Reflect" },
];

export const PhoneMockup = () => {
  return (
    <div
      className="relative phone-glow"
      data-testid="app-phone-mockup"
    >
      <div className="relative mx-auto w-[300px] sm:w-[340px] h-[640px] sm:h-[700px] rounded-[3rem] border-[10px] border-zinc-800/80 bg-black shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 w-28 h-6 bg-black rounded-b-2xl" />

        {/* Soundscape background visual */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0820] via-[#120b25] to-[#070912]" />
          <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-violet-500/30 blur-3xl anim-drift1" />
          <div className="absolute bottom-0 -right-10 w-64 h-64 rounded-full bg-amber-400/20 blur-3xl anim-drift3" />
          <div className="absolute top-1/3 left-1/4 w-48 h-48 rounded-full bg-blue-400/20 blur-3xl anim-drift2" />
          <div className="starfield" />
        </div>

        {/* Inner UI */}
        <div className="relative z-10 h-full pt-10 pb-6 px-5 flex flex-col gap-4">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                Tonight
              </div>
              <div className="font-heading text-white text-lg leading-tight">
                Choose a groove
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
              <Sparkles size={14} className="text-amber-300" />
            </div>
          </div>

          {/* Mood selection */}
          <div className="grid grid-cols-4 gap-2">
            {moods.map(({ icon: Icon, label }, idx) => (
              <div
                key={label}
                className={`rounded-xl px-2 py-3 flex flex-col items-center gap-1 ${
                  idx === 0
                    ? "bg-white/10 border border-white/20"
                    : "bg-white/[0.03] border border-white/10"
                }`}
              >
                <Icon size={16} className="text-white" />
                <span className="text-[10px] text-zinc-300">{label}</span>
              </div>
            ))}
          </div>

          {/* Music card */}
          <div className="rounded-2xl p-4 glass-strong flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 via-blue-500 to-amber-400 shrink-0 shadow-lg" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-zinc-400">Now playing</div>
              <div className="text-sm text-white truncate">Twilight Drift</div>
              <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full w-[40%] bg-gradient-to-r from-amber-300 to-violet-400" />
              </div>
            </div>
            <div className="eq" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>

          {/* Chat area */}
          <div className="flex-1 rounded-2xl glass p-3 flex flex-col gap-2 overflow-hidden">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">
              Reflection
            </div>
            <div className="self-start max-w-[80%] px-3 py-2 rounded-2xl rounded-bl-sm bg-white/[0.06] border border-white/10 text-[12px] text-zinc-100">
              Where would you like to go tonight — calm, focus, or somewhere
              softer?
            </div>
            <div className="self-end max-w-[80%] px-3 py-2 rounded-2xl rounded-br-sm bg-gradient-to-br from-violet-500/60 to-blue-500/40 border border-white/10 text-[12px] text-white">
              I need to slow down and breathe.
            </div>
            <div className="self-start max-w-[80%] px-3 py-2 rounded-2xl rounded-bl-sm bg-white/[0.06] border border-white/10 text-[12px] text-zinc-100">
              Let&apos;s drift into a calm groove. Close your eyes — I&apos;ll set the
              atmosphere.
            </div>

            <div className="mt-auto flex items-center gap-2 px-3 py-2 rounded-xl bg-black/40 border border-white/10">
              <span className="text-[11px] text-zinc-500 flex-1">
                Say what you feel…
              </span>
              <Send size={14} className="text-amber-300" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneMockup;
